import { imgSrc } from "@/lib/api";
import { SectionHead, PhoneShot, GRAD } from "@/components/landing/Kit";
import { Link } from "react-router-dom";
import { UserPlus, Coins, Gift, FileText, UserCircle, Image, Heart, Users, Mail, UsersRound, ArrowRight, Sparkles, PlayCircle, MessageCircle, Flame } from "lucide-react";

export const Section = ({ id, eyebrow, title, sub, children, className = "", side, flip }) => (
  <section id={id} className={`max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14 scroll-mt-20 ${className}`}>
    {side ? (
      <div className={`grid gap-10 items-start ${flip ? "lg:grid-cols-[300px_1fr]" : "lg:grid-cols-[1fr_300px]"}`}>
        <div className={`min-w-0 ${flip ? "lg:order-2" : ""}`}><SectionHead eyebrow={eyebrow} title={title} lead={sub} className="mb-8 sm:mb-10" />{children}</div>
        <div className={`hidden lg:block sticky top-24 pt-6 ${flip ? "lg:order-1" : ""}`}>{side}</div>
      </div>
    ) : <><SectionHead eyebrow={eyebrow} title={title} lead={sub} className="mb-8 sm:mb-10" />{children}</>}
  </section>
);

const STEPS = [
  { icon: UserPlus, title: "Vernetzen", text: "Erstelle dein Profil, finde Freunde, veröffentliche Beiträge, teile Fotos und tausche dich mit anderen Mitgliedern aus." },
  { icon: Coins, title: "Punkte sammeln", text: "Punkte gibt's für Beiträge, Kommentare, tägliche Missionen, Login-Serien, eingeladene Freunde, Community-Aufgaben – und für jedes Spiel, das du spielst." },
  { icon: Gift, title: "Geschenke erhalten", text: "Sammle genügend Punkte und tausche sie gegen echte Geschenke aus unserem Prämienshop ein." },
];

export const Steps = () => (
  <Section id="so-gehts" eyebrow="So funktioniert itzPlay" title={<>In 3 Schritten zu deinem <span className={GRAD}>ersten Geschenk.</span></>}>
    <ol className="grid md:grid-cols-3 gap-5" data-testid="landing-steps">
      {STEPS.map((s, i) => (
        <li key={s.title} className="relative bg-white rounded-3xl p-7 border border-pink-100 shadow-sm hover:shadow-xl hover:shadow-rose-100 hover:-translate-y-1 transition-[transform,box-shadow] duration-300" data-testid={`landing-step-${i + 1}`}>
          <span className="absolute top-6 right-6 font-display text-6xl font-extrabold text-rose-100">{i + 1}</span>
          <span className="w-12 h-12 rounded-2xl pink-gradient text-white flex items-center justify-center shadow-lg shadow-rose-200"><s.icon className="w-6 h-6" /></span>
          <p className="mt-6 text-xs font-bold uppercase tracking-wider text-rose-500">Schritt {i + 1}</p>
          <h3 className="text-xl font-bold mt-1">{s.title}</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.text}</p>
        </li>
      ))}
    </ol>
  </Section>
);

const FEATURES = [
  { icon: FileText, title: "Community-Beiträge", text: "Ein gemeinsamer Newsfeed mit Texten und bis zu 4 Fotos pro Beitrag." },
  { icon: UserCircle, title: "Eigene Profile", text: "Titelbild, Bio, Wohnort, Geburtstag und deine Social-Media-Links." },
  { icon: PlayCircle, title: "Fotos & Stories", text: "Teile Fotos und Stories mit deinen Freunden und der ganzen Community." },
  { icon: Heart, title: "Likes & Kommentare", text: "Reagiere auf Beiträge und komm mit anderen ins Gespräch." },
  { icon: Users, title: "Freundschaften", text: "Sende Freundschaftsanfragen und vergleiche dich in der Freunde-Rangliste." },
  { icon: Mail, title: "Private Nachrichten", text: "Schreib deinen Freunden direkt – mit Push-Mitteilung aufs Handy." },
];

export const Features = () => (
  <div>
    <Section id="netzwerk" eyebrow="Community" title={<>Mehr als nur <span className={GRAD}>Spielen</span></>} sub="Bei itzPlay spielst du nicht allein: Poste deine Erfolge, finde Mitspieler und werde für jede Aktivität belohnt."
      side={<PhoneShot src="/screens/feed.jpg" alt="Der itzPlay-Feed in der App" testId="landing-phone-feed" chips={[{ icon: Heart, title: "128 Likes", sub: "auf deinen Beitrag" }, { icon: Users, title: "Neue Freundin", sub: "Anfrage angenommen" }]} />}>
      <div className="grid sm:grid-cols-2 gap-4" data-testid="landing-features">
        {FEATURES.map((f) => (
          <div key={f.title} className="group rounded-3xl p-6 bg-white border border-pink-100 hover:border-rose-300 transition-colors">
            <f.icon className="w-7 h-7 text-rose-500 group-hover:scale-110 transition-transform" />
            <h3 className="mt-4 font-bold text-lg">{f.title}</h3>
            <p className="mt-1 text-sm text-slate-600">{f.text}</p>
          </div>
        ))}
        <div className="rounded-3xl p-6 border-2 border-dashed border-pink-200 bg-pink-50/40" data-testid="landing-feature-groups">
          <div className="flex items-center justify-between"><UsersRound className="w-7 h-7 text-slate-400" /><span className="px-2.5 py-1 rounded-full bg-white border border-pink-200 text-[11px] font-bold text-rose-500">Demnächst</span></div>
          <h3 className="mt-4 font-bold text-lg text-slate-500">Gruppen</h3>
          <p className="mt-1 text-sm text-slate-500">Gründe Gruppen zu deinen Lieblingsthemen.</p>
        </div>
      </div>
    </Section>
  </div>
);

const EARN = [
  { icon: FileText, label: "Beitrag posten", key: "points_post" },
  { icon: MessageCircle, label: "Kommentar schreiben", key: "points_comment" },
  { icon: Flame, label: "Täglicher Login", key: "points_daily" },
  { icon: Sparkles, label: "Alle Tagesmissionen", key: "points_missions" },
  { icon: UserPlus, label: "Freund eingeladen", key: "points_referral" },
];

const DEMO_MISSIONS = [["Login-Bonus abholen", true], ["1 Beitrag posten", true], ["3 Kommentare schreiben", false], ["Profil vervollständigen", false]];

export const RewardsShowcase = ({ settings, reward }) => (
  <Section id="punkte" eyebrow="Unser Alleinstellungsmerkmal" title={<>Deine Aktivität wird <span className={GRAD}>belohnt!</span></>}>
    <div className="grid lg:grid-cols-2 gap-6 items-start">
      <div className="rounded-[2rem] p-6 sm:p-8 text-white bg-gradient-to-br from-rose-500 via-pink-500 to-fuchsia-500 shadow-2xl shadow-rose-200 space-y-6" data-testid="landing-points-demo">
        <div className="flex items-end justify-between gap-4">
          <div><p className="text-xs uppercase font-bold tracking-wider opacity-80">Punktestand</p><p className="font-display text-5xl font-extrabold">1.240</p></div>
          <div className="text-right"><p className="text-xs uppercase font-bold tracking-wider opacity-80">Heute</p><p className="font-display text-2xl font-bold">+38</p></div>
        </div>
        <div>
          <div className="flex justify-between text-xs font-semibold mb-1.5"><span className="truncate">Nächstes Geschenk: {reward?.title || "Geschenk"}</span><span className="shrink-0 ml-2">noch {Math.max(0, (reward?.cost || 1500) - 1240)} P.</span></div>
          <div className="h-2.5 rounded-full bg-white/25"><div className="h-full rounded-full bg-white" style={{ width: `${Math.min(100, Math.round((1240 / (reward?.cost || 1500)) * 100))}%` }} /></div>
        </div>
        <div className="rounded-2xl bg-white/15 backdrop-blur p-4 space-y-2">
          <p className="text-xs uppercase font-bold tracking-wider opacity-90">Tägliche Missionen · +{settings?.points_missions ?? 15} Bonus</p>
          {DEMO_MISSIONS.map(([t, done]) => (
            <p key={t} className="flex items-center gap-2 text-sm"><span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${done ? "bg-white text-rose-500" : "border-2 border-white/60"}`}>{done && "✓"}</span><span className={done ? "line-through opacity-75" : ""}>{t}</span></p>
          ))}
        </div>
        <p className="text-[11px] opacity-75">Beispielansicht – so sieht dein Punktekonto nach dem Login aus.</p>
      </div>
      <ul className="grid grid-cols-2 gap-3" data-testid="landing-earn-list">
        {EARN.map((e) => (
          <li key={e.key} className="bg-white rounded-2xl p-4 sm:p-5 border border-pink-100 flex flex-col gap-3">
            <e.icon className="w-6 h-6 text-rose-500" />
            <p className="text-sm font-semibold leading-tight">{e.label}</p>
            <p className="font-display text-2xl font-extrabold text-amber-600">+{settings?.[e.key] ?? "…"}</p>
          </li>
        ))}
      </ul>
    </div>
  </Section>
);

export const GiftShop = ({ rewards, user }) => (
  <div>
    <Section id="geschenke" eyebrow="Prämienshop" title={<>Deine Punkte. <span className={GRAD}>Deine Geschenke.</span></>} sub="Echte Prämien, die du mit deinen gesammelten Punkten einlösen kannst." flip
      side={<PhoneShot src="/screens/geschenke.jpg" alt="Der Geschenke-Shop in der App" tilt={-2} testId="landing-phone-gifts" chips={[{ icon: Gift, title: "Eingelöst!", sub: "Gutschein unterwegs" }, { icon: Coins, title: "+10 Punkte", sub: "Video geschaut" }]} />}>
      <div className="grid sm:grid-cols-2 gap-5" data-testid="landing-gift-grid">
        {!rewards && [0, 1, 2].map((i) => <div key={i} className="rounded-3xl bg-rose-50 aspect-[4/3] animate-pulse" data-testid="landing-gift-skeleton" />)}
        {rewards?.length === 0 && <p className="text-sm text-slate-400">Geschenke werden gerade aufgefüllt.</p>}
        {rewards?.map((r) => {
          const missing = user ? Math.max(0, r.cost - (user.points || 0)) : null;
          const soldOut = r.available === 0;
          return (
            <article key={r.id} className="group bg-white rounded-3xl overflow-hidden border border-pink-100 hover:shadow-2xl hover:shadow-rose-100 hover:-translate-y-1 transition-[transform,box-shadow] duration-300" data-testid={`landing-gift-${r.id}`}>
              <div className="aspect-[4/3] bg-rose-50 overflow-hidden relative">
                {r.image && <img src={imgSrc(r.image)} alt={r.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                <span className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-bold ${soldOut ? "bg-slate-800 text-white" : "bg-white/90 text-emerald-700"}`}>{soldOut ? "Vergriffen" : r.available == null ? "Verfügbar" : `${r.available} verfügbar`}</span>
              </div>
              <div className="p-5 space-y-3">
                <h3 className="font-bold text-lg leading-snug">{r.title}</h3>
                <p className="flex items-center gap-1.5 font-display text-xl font-extrabold text-amber-600"><Coins className="w-5 h-5" /> {r.cost.toLocaleString("de-DE")} Punkte</p>
                {missing !== null && <p className="text-xs font-semibold text-rose-600" data-testid={`landing-gift-missing-${r.id}`}>{missing === 0 ? "Du kannst es jetzt einlösen! 🎉" : `Dir fehlen noch ${missing.toLocaleString("de-DE")} Punkte`}</p>}
                <Link to={user ? "/geschenke" : "/registrieren"} data-testid={`landing-gift-view-${r.id}`} className="h-11 rounded-full bg-rose-50 text-rose-600 font-bold text-sm flex items-center justify-center gap-2 hover:bg-rose-100 transition-colors">Ansehen <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </article>
          );
        })}
      </div>
    </Section>
  </div>
);
